import * as R from 'ramda'
import { useReducer, useCallback, useEffect } from 'react'
import effects from 'app/effects/effects'
import { TYPE_POLL } from 'app/constants/messageType'

const START_LOADING = 'START_LOADING'
const SET_LANGUAGE_PREVIEW = 'SET_LANGUAGE_PREVIEW'
const SET_SORTING_LANGUAGE_PREVIEW = 'SET_SORTING_LANGUAGE_PREVIEW'
const SET_LANGUAGE_PREVIEW_ERROR = 'SET_LANGUAGE_PREVIEW_ERROR'

const initialState = {
  detectLanguage: [],
  sortLanguage: [],
  isLoading: false,
  isShowLanguages: false,
  error: undefined,
}

function reducer(state, action) {
  switch (action.type) {
    case SET_LANGUAGE_PREVIEW: {
      return R.mergeRight(state, {
        detectLanguage: action.detectLanguage,
        isLoading: false,
        error: undefined,
      })
    }
    case SET_SORTING_LANGUAGE_PREVIEW: {
      return R.mergeRight(state, {
        sortLanguage: action.sortLanguage,
        isLoading: false,
        error: undefined,
      })
    }
    case SET_LANGUAGE_PREVIEW_ERROR:
      return R.mergeRight(state, {
        detectLanguage: undefined,
        isLoading: false,
        error: action.error,
      })
    case START_LOADING:
      return R.mergeRight(state, {
        isLoading: true,
        error: undefined,
      })
    default:
      return state
  }
}

export const useLanguagePreview = (
  notification,
  subscriberLanguage,
  defaultLanguage,
) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const detectLanguage = useCallback(() => {
    if (state.isLoading) return
    dispatch({ type: START_LOADING })

    const payload = R.equals(notification.type, TYPE_POLL)
      ? R.pick(['question', 'options'], notification)
      : R.pick(['title', 'body'], notification)

    return effects.apis
      .detectLanguage(payload)
      .then(
        R.compose(
          dispatch,
          R.mergeRight({ type: SET_LANGUAGE_PREVIEW }),
          R.objOf('detectLanguage'),
        ),
      )
  }, [notification, dispatch, state.isLoading])

  const findEqualyLanguage = (data, find) => {
    return R.find(R.propEq('id', find))(data)
  }

  const findLanguageID = (data, find) => {
    return R.findIndex(R.propEq('id', find))(data)
  }

  const handleLanguage = (data) => {
    let arrayLanguaged = []
    const sortData = R.compose(R.sortBy(R.prop('name')))(data)

    const getLanguage = !R.isEmpty(state.detectLanguage)
      ? R.clone(state.detectLanguage)
      : R.clone(defaultLanguage)

    // ** detectLanguage ** //
    const equalyLanguage = findEqualyLanguage(
      sortData,
      state.detectLanguage.id || defaultLanguage.id,
    )

    const findDetectLanguageID = !R.isNil(equalyLanguage)
      ? findLanguageID(sortData, state.detectLanguage.id || defaultLanguage.id)
      : 0

    const detectLanguaged = !R.isNil(equalyLanguage)
      ? sortData.splice(findDetectLanguageID, 1)
      : [{ ...getLanguage, count: 0 }]

    // ** defaultLanguage ** //
    const equalyDefaultLanguage = findEqualyLanguage(
      sortData,
      defaultLanguage.id,
    )

    const findDefaultLanguageID =
      !R.isNil(equalyDefaultLanguage) &&
      findLanguageID(sortData, defaultLanguage.id)

    const defaultLanguaged = !R.isNil(equalyDefaultLanguage)
      ? sortData.splice(findDefaultLanguageID, 1)
      : []

    switch (true) {
      case R.equals(detectLanguaged[0].id, defaultLanguage.id) ||
        R.isNil(detectLanguaged):
        arrayLanguaged.push(detectLanguaged[0])
        break
      case R.isNil(defaultLanguaged):
        arrayLanguaged.push(defaultLanguaged[0])
        break
      case !R.isNil(detectLanguaged) && R.isEmpty(defaultLanguaged):
        arrayLanguaged.push(detectLanguaged[0])
        break
      default:
        arrayLanguaged.push(detectLanguaged[0], defaultLanguaged[0])
        break
    }

    R.compose(
      dispatch,
      R.mergeRight({ type: SET_SORTING_LANGUAGE_PREVIEW }),
      R.objOf('sortLanguage'),
      R.defaultTo([]),
      R.concat(arrayLanguaged),
    )(sortData)
  }

  useEffect(() => {
    detectLanguage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const { data } = subscriberLanguage
    if (!R.isNil(data)) handleLanguage(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriberLanguage, state.detectLanguage])

  return {
    detectLanguage: state.detectLanguage,
    sortLanguage: state.sortLanguage,
  }
}
